import { useCallback, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

const POSTCODE_REGEX = /^\d{4}\s?[A-Za-z]{2}$/;

export function isValidPostcode(value: string | null | undefined): boolean {
  if (!value) return false;
  return POSTCODE_REGEX.test(value.trim());
}

export interface AddressLookupResult {
  street: string;
  city: string;
}

interface State {
  isLoading: boolean;
  error: string | null;
  result: AddressLookupResult | null;
  isManualMode: boolean;
}

const cache = new Map<string, AddressLookupResult>();

export function useAddressLookup() {
  const [state, setState] = useState<State>({
    isLoading: false,
    error: null,
    result: null,
    isManualMode: false,
  });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const lookup = useCallback((postcode: string, houseNumber: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (!isValidPostcode(postcode) || !houseNumber.trim()) {
      setState({ isLoading: false, error: null, result: null, isManualMode: false });
      return;
    }

    const cacheKey = `${postcode.replace(/\s/g, '').toUpperCase()}::${houseNumber.trim()}`;
    const cached = cache.get(cacheKey);
    if (cached) {
      setState({ isLoading: false, error: null, result: cached, isManualMode: false });
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setState((s) => ({ ...s, isLoading: true, error: null }));
      const controller = new AbortController();
      abortRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers: Record<string, string> = {};
        if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

        const url = `/api/lookup?pc=${encodeURIComponent(postcode)}&hn=${encodeURIComponent(houseNumber)}`;
        const res = await fetch(url, { headers, signal: controller.signal });
        clearTimeout(timeoutId);

        if (controller.signal.aborted) return;

        if (!res.ok) {
          setState({ isLoading: false, error: 'Adres niet gevonden', result: null, isManualMode: true });
          return;
        }

        const data = (await res.json()) as { ok: boolean; street?: string; city?: string };
        if (!data.ok || !data.street || !data.city) {
          setState({ isLoading: false, error: 'Adres niet gevonden', result: null, isManualMode: true });
          return;
        }

        const result: AddressLookupResult = { street: data.street, city: data.city };
        cache.set(cacheKey, result);
        setState({ isLoading: false, error: null, result, isManualMode: false });
      } catch (err) {
        clearTimeout(timeoutId);
        if (controller.signal.aborted) {
          setState({ isLoading: false, error: null, result: null, isManualMode: true });
        } else {
          setState({
            isLoading: false,
            error: err instanceof Error ? err.message : 'Adres zoeken mislukt',
            result: null,
            isManualMode: true,
          });
        }
      }
    }, 300);
  }, []);

  const reset = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    setState({ isLoading: false, error: null, result: null, isManualMode: false });
  }, []);

  return { ...state, lookup, reset };
}
